import { useState, useEffect, useRef } from 'react';
import { useLanguage } from '@/lib/i18n';
import { X, Send, Bot, Loader2, LifeBuoy } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PublicChatWidget({ isOpen, onClose }) {
  const { lang } = useLanguage();
  const ja = lang === 'ja';
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [lastUserQuestion, setLastUserQuestion] = useState('');
  const [lastWasUncertain, setLastWasUncertain] = useState(false);
  const [sessionId] = useState(() => {
    const stored = localStorage.getItem('jtr_chat_session');
    if (stored) return stored;
    const id = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
    localStorage.setItem('jtr_chat_session', id);
    return id;
  });
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen && messages.length === 0) loadHistory();
    if (isOpen && inputRef.current) setTimeout(() => inputRef.current?.focus(), 300);
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadHistory = async () => {
    try {
      const res = await fetch(`${API}/api/chat/widget/history/${sessionId}`);
      if (res.ok) {
        const data = await res.json();
        if (data.messages && data.messages.length > 0) {
          setMessages(data.messages);
          // Find last user message to seed escalation prefill if needed.
          const lastUser = [...data.messages].reverse().find(m => m.role === 'user');
          if (lastUser) setLastUserQuestion(lastUser.content);
          return;
        }
      }
    } catch { /* ignore */ }
    setMessages([{
      role: 'assistant',
      content: ja
        ? 'こんにちは！Japan Toll Receiptsサポートアシスタントです。ETC利用レポートやサービスについて、何でもお尋ねください。回答できない場合は下の「担当スタッフに問い合わせる」ボタンから直接お問い合わせいただけます。'
        : "Hi! I'm the Japan Toll Receipts support assistant. Ask me about ETC toll reports or our service. If I can't answer, click the 'Get human help' button below to send your question to support.",
      timestamp: new Date().toISOString()
    }]);
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;
    const text = input.trim();
    const userMsg = { role: 'user', content: text, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setLastUserQuestion(text);
    setLastWasUncertain(false);
    setInput('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/chat/widget/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          session_id: sessionId,
          message: text,
          language: lang,
          page_url: typeof window !== 'undefined' ? window.location.href : '',
          source_page: typeof window !== 'undefined' ? window.location.pathname : 'chat_widget',
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: data.reply,
          timestamp: new Date().toISOString(),
          uncertain: !!data.uncertain,
        }]);
        setLastWasUncertain(!!data.uncertain);
      } else {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: ja ? '申し訳ございません。エラーが発生しました。「担当スタッフに問い合わせる」をご利用ください。' : "Sorry, an error occurred. Please use 'Get human help' to reach a person.",
          timestamp: new Date().toISOString(),
          uncertain: true,
        }]);
        setLastWasUncertain(true);
      }
    } catch {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: ja ? '接続エラーが発生しました。「担当スタッフに問い合わせる」をご利用ください。' : "Connection error. Please use 'Get human help' to reach a person.",
        timestamp: new Date().toISOString(),
        uncertain: true,
      }]);
      setLastWasUncertain(true);
    } finally {
      setLoading(false);
    }
  };

  const buildContextSummary = () => {
    return messages.slice(-6).map(m => `${m.role === 'user' ? (ja ? 'お客様' : 'Customer') : (ja ? 'アシスタント' : 'Assistant')}: ${m.content}`).join('\n');
  };

  const escalateToSupport = () => {
    const lastQ = lastUserQuestion || (messages.filter(m => m.role === 'user').pop()?.content) || '';
    const ctxSummary = buildContextSummary();
    const prefilledMessage = lastQ
      ? (ja
          ? `チャットアシスタントで以下の質問への回答が得られませんでした：

${lastQ}

--- 会話履歴 ---
${ctxSummary}`
          : `I was using the chat assistant and could not get an answer to this question:\n\n${lastQ}\n\n--- Recent chat context ---\n${ctxSummary}`)
      : (ja ? 'チャットアシスタントからサポートへ送信しています。' : 'Sending this question to support from the chat assistant.');

    const detail = {
      subject: ja ? 'チャットからの担当者対応依頼' : 'Chat escalation: customer needs help',
      message: prefilledMessage,
      last_chat_question: lastQ,
      chat_context_summary: ctxSummary,
      escalated_to_support: true,
      source_page: 'chat_widget',
    };
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('jtr:open-support', { detail }));
    }
    if (onClose) onClose();
  };

  if (!isOpen) return null;

  // Show "Get human help" button if last user message exists OR last AI was uncertain.
  const showEscalateBtn = !!lastUserQuestion || lastWasUncertain;

  return (
    <div className="fixed inset-0 md:inset-auto md:bottom-24 md:right-20 md:w-[380px] md:h-[560px] bg-white md:rounded-2xl shadow-2xl border border-slate-200 z-[9999] flex flex-col overflow-hidden animate-slide-up" data-testid="chat-widget">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1e2a4a] to-[#2d3b6a] px-4 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
            <Bot className="w-4 h-4 text-white" />
          </div>
          <div>
            <p className="text-sm font-bold text-white">{ja ? 'サポートアシスタント' : 'Support Assistant'}</p>
            <p className="text-[10px] text-blue-200">{ja ? 'オンライン。即座に返信します' : 'Online — typically replies instantly'}</p>
          </div>
        </div>
        <button onClick={onClose} className="p-1 hover:bg-white/10 rounded transition-colors" data-testid="chat-widget-close" aria-label="close">
          <X className="w-4 h-4 text-white/80" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] px-3 py-2 rounded-xl text-sm leading-relaxed whitespace-pre-wrap ${
              msg.role === 'user'
                ? 'bg-[#1e2a4a] text-white rounded-br-sm'
                : 'bg-slate-100 text-slate-800 rounded-bl-sm'
            }`}>
              {msg.content}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-slate-100 px-4 py-2.5 rounded-xl rounded-bl-sm">
              <div className="flex gap-1">
                <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Escalate banner — emphasize if last reply was uncertain */}
      {showEscalateBtn && (
        <div className={`px-3 py-2 border-t flex-shrink-0 ${lastWasUncertain ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-100'}`}>
          <button
            type="button"
            onClick={escalateToSupport}
            className={`w-full flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-bold transition-colors ${
              lastWasUncertain
                ? 'bg-amber-600 hover:bg-amber-700 text-white'
                : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
            }`}
            data-testid="chat-get-human-help-btn"
          >
            <LifeBuoy className="w-3.5 h-3.5" strokeWidth={2} />
            {ja ? '担当スタッフに問い合わせる' : 'Get human help'}
          </button>
          <p className="text-[10px] text-slate-500 mt-1.5 leading-snug">
            {ja
              ? 'サポートはメールで対応します。パスワードはメッセージに含めないでください。'
              : 'Support replies by email. Please do not include passwords in your message.'}
          </p>
        </div>
      )}

      {/* Input — safe-area padding (PPA-D03) so the send button clears the
          home-indicator gesture bar on notch devices when the chat is the
          full-screen mobile takeover (inset-0). */}
      <form onSubmit={sendMessage} className="p-3 border-t border-slate-100 flex-shrink-0" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
        <div className="flex gap-2">
          <input
            ref={inputRef}
            id="public-chat-message" name="message" autoComplete="off"
            aria-label={ja ? 'チャットメッセージ' : 'Chat message'}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            className="flex-1 px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-300"
            placeholder={ja ? 'メッセージを入力...' : 'Type a message...'}
            disabled={loading}
            data-testid="chat-input"
          />
          <button
            type="submit"
            disabled={loading || !input.trim()}
            className="p-2 bg-[#1e2a4a] text-white rounded-xl hover:bg-[#2d3b6a] disabled:opacity-50 transition-colors"
            data-testid="chat-send-btn"
            aria-label="send"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </form>
    </div>
  );
}
